import { useState } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import ArticleLink from '@/components/feature/ArticleLink';
import ScrollReveal from '@/components/base/ScrollReveal';
import { analysisCategories } from '@/mocks/forexAnalysisPage';

const spanClass: Record<number, string> = {
  4: 'lg:col-span-4',
  5: 'lg:col-span-5',
  7: 'lg:col-span-7',
  8: 'lg:col-span-8',
};

const toneClass = {
  primary: {
    num: 'text-primary-600',
    chip: 'bg-primary-100 text-primary-800',
    bar: 'bg-primary-500',
  },
  accent: {
    num: 'text-accent-600',
    chip: 'bg-accent-100 text-accent-800',
    bar: 'bg-accent-500',
  },
  secondary: {
    num: 'text-secondary-700',
    chip: 'bg-secondary-100 text-secondary-900',
    bar: 'bg-secondary-500',
  },
};

const INITIAL_LESSONS = 3;

export default function TutorialLibrary() {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const totalLessons = analysisCategories.reduce((sum, category) => sum + category.lessons.length, 0);

  return (
    <section id="tutorial-library" className="scroll-mt-[7.5rem] md:scroll-mt-[9rem] border-b border-background-200 bg-background-50">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20 lg:px-10">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between lg:gap-12">
          <SectionHeading
            eyebrow="คลังบทความ"
            title="คลังบทความการวิเคราะห์ Forex"
            subtitle="แบ่งเป็นหกหมวดตามลำดับการอ่านกราฟจริง เปิดหมวดไหนก็เห็นหัวข้อที่ควรอ่านก่อน และกดดูบทความทั้งหมดของหมวดนั้นได้ทันที"
          />
          <dl className="flex shrink-0 items-center gap-8 border-t border-background-200 pt-4 lg:border-0 lg:pt-0">
            <div>
              <dt className="text-xs text-foreground-500">หมวดบทความ</dt>
              <dd className="font-num text-2xl font-semibold text-foreground-950">{analysisCategories.length}</dd>
            </div>
            <div>
              <dt className="text-xs text-foreground-500">หัวข้อทั้งหมด</dt>
              <dd className="font-num text-2xl font-semibold text-foreground-950">{totalLessons}</dd>
            </div>
          </dl>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 md:mt-12 md:grid-cols-2 md:gap-5 lg:grid-cols-12">
          {analysisCategories.map((category, index) => {
            const tone = toneClass[category.tone];
            const isOpen = expanded[category.id] ?? false;
            const visibleLessons = isOpen ? category.lessons : category.lessons.slice(0, INITIAL_LESSONS);
            const isFeatured = category.colSpan >= 7;

            return (
              <ScrollReveal
                as="article"
                key={category.id}
                delay={index * 60}
                className={`${spanClass[category.colSpan]} flex flex-col rounded-xl border border-background-200 bg-background-50 p-5 md:p-6`}
              >
                <div id={`tutorial-${category.id}`} className="scroll-mt-[7.5rem] md:scroll-mt-[9rem]">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-baseline gap-3">
                      <span className={`font-num text-2xl font-semibold leading-none md:text-[28px] ${tone.num}`}>
                        {category.no}
                      </span>
                      <h3
                        className={`font-heading font-semibold leading-snug text-foreground-950 ${
                          isFeatured ? 'text-lg md:text-[22px]' : 'text-base md:text-lg'
                        }`}
                      >
                        {category.title}
                      </h3>
                    </div>
                    <span className={`mt-1 hidden shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold sm:inline-flex ${tone.chip}`}>
                      {category.lessons.length} บทความ
                    </span>
                  </div>

                  <p className={`mt-3 text-sm leading-relaxed text-foreground-600 ${isFeatured ? 'md:max-w-2xl' : ''}`}>
                    {category.intro}
                  </p>
                </div>

                <ul className="mt-5 flex flex-1 flex-col divide-y divide-background-200 border-t border-background-200">
                  {visibleLessons.map((lesson) => (
                    <li key={lesson.title}>
                      <ArticleLink
                        to={`/${lesson.slug}`}
                        className="group/link flex items-start gap-2.5 py-2.5 pr-1 transition-colors"
                        aria-label={`เปิดบทความ ${lesson.title}`}
                      >
                        <span className="mt-[3px] flex h-4 w-4 shrink-0 items-center justify-center text-foreground-300 transition-colors group-hover/link:text-primary-600">
                          <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
                        </span>
                        <span className="min-w-0 flex-1 text-[13px] leading-relaxed text-foreground-700 transition-colors group-hover/link:text-primary-700">
                          {lesson.title}
                        </span>
                      </ArticleLink>
                    </li>
                  ))}
                </ul>

                {category.lessons.length > INITIAL_LESSONS && (
                  <button
                    type="button"
                    onClick={() => setExpanded((prev) => ({ ...prev, [category.id]: !isOpen }))}
                    aria-expanded={isOpen}
                    className="mt-4 flex cursor-pointer items-center gap-1.5 self-start border-t border-background-200 pt-3 text-xs font-semibold text-foreground-700 transition-colors hover:text-primary-700"
                  >
                    <i className={isOpen ? 'ri-subtract-line text-sm' : 'ri-add-line text-sm'} aria-hidden="true" />
                    {isOpen ? 'ย่อบทความ' : `ดูบทความทั้งหมด (${category.lessons.length})`}
                  </button>
                )}
              </ScrollReveal>
            );
          })}
        </div>

        <div className="mt-6 flex items-start gap-2.5 rounded-lg border border-background-200 bg-background-100/70 p-4">
          <i className="ri-book-open-line mt-0.5 text-base text-foreground-500" aria-hidden="true" />
          <p className="text-xs leading-relaxed text-foreground-600">
            ทั้ง {totalLessons} หัวข้อในคลังเปิดอ่านได้ครบแล้ว กดชื่อบทความเพื่อเข้าไปอ่านฉบับเต็ม
            ซึ่งมีตัวอย่างกราฟ คำอธิบายทีละขั้น และคำถามที่พบบ่อยท้ายบทความ
          </p>
        </div>
      </div>
    </section>
  );
}